import { useState } from 'react';
import { Printer } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import type { GoodsReceipt, PurchaseOrder } from '@/api/endpoints';
import { Alert, Button, LoadingState, Modal } from '@/components/ui';
import { printDocument } from '@/lib/printing';
import { purchaseDocument } from '@/lib/receiptDocuments';

interface SlipLine {
  id: string;
  label: string;
  sku: string;
  quantity: string;
  unitCost: string;
  lineTotal: string;
  rejected?: string;
}

/**
 * A printable slip for a purchase order or a goods receipt.
 *
 * Both are documents a supplier or a storeman is handed on paper — the order
 * that says what was asked for, the receipt that says what actually arrived.
 * They share a shape, so they share a slip, laid out for the same 58mm roll as
 * every other receipt in the system.
 */
export function PurchaseSlip({
  order,
  receipt,
  loading,
  onClose,
}: {
  order?: PurchaseOrder | null;
  receipt?: GoodsReceipt | null;
  loading?: boolean;
  onClose: () => void;
}) {
  const format = useFormat();
  const context = useAppSelector((state) => state.tenant.context);
  const organization = context?.organization;
  const [printing, setPrinting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const isReceipt = Boolean(receipt);
  const document = receipt ?? order;

  const address = [organization?.addressLine1, organization?.addressLine2, organization?.city]
    .filter(Boolean)
    .join(', ');

  const lines: SlipLine[] = receipt
    ? (receipt.items ?? []).map((item) => ({
        id: item.id,
        label: item.variant.product.name,
        sku: item.variant.sku,
        quantity: item.quantity,
        unitCost: item.unitCost,
        lineTotal: item.lineTotal,
        rejected: Number(item.rejectedQty) > 0 ? item.rejectedQty : undefined,
      }))
    : (order?.items ?? []).map((item) => ({
        id: item.id,
        // An order line carries its own description, which is what was agreed
        // with the supplier even if the product has been renamed since.
        label: item.description,
        sku: '',
        quantity: item.quantity,
        unitCost: item.unitCost,
        lineTotal: item.lineTotal,
      }));

  const total = receipt ? receipt.totalCost : order?.total ?? '0';

  const row = (label: string, value?: string | null) =>
    value ? (
      <div className="flex justify-between gap-2">
        <span>{label}</span>
        <span className="text-right">{value}</span>
      </div>
    ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={isReceipt ? 'Goods receipt' : 'Purchase order'}
      description={document ? `${document.number} · ${document.supplier.name}` : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            icon={Printer}
            disabled={!document}
            loading={printing}
            onClick={() => {
              if (!document) return;
              setFailure(null);
              setPrinting(true);
              void printDocument(
                purchaseDocument(document, isReceipt, organization ?? {}, format),
              )
                .then((result) => {
                  if (!result.printed) setFailure(result.error);
                })
                .finally(() => setPrinting(false));
            }}
          >
            Print
          </Button>
        </div>
      }
    >
      {failure && <div className="mb-3"><Alert tone="danger">{failure}</Alert></div>}

      {loading || !document ? (
        <LoadingState />
      ) : (
        <div
          id="receipt-print-area"
          className="mx-auto max-w-[58mm] bg-white p-3 font-mono text-[10px] leading-snug text-black"
        >
          <div className="text-center">
            <p className="text-xs font-bold uppercase">{organization?.name}</p>
            {address && <p>{address}</p>}
            {organization?.phone && <p>{organization.phone}</p>}
          </div>

          <hr className="my-2 border-dashed border-black" />
          <p className="text-center font-bold">
            {isReceipt ? 'GOODS RECEIVED NOTE' : 'PURCHASE ORDER'}
          </p>
          <hr className="my-2 border-dashed border-black" />

          {row('Number', document.number)}
          {row('Supplier', document.supplier.name)}
          {row(
            isReceipt ? 'Received' : 'Raised',
            formatDateTime(receipt ? receipt.receivedAt : order?.createdAt ?? '', format),
          )}
          {receipt && row('Warehouse', receipt.warehouse.name)}
          {receipt && row('Against order', receipt.order?.number)}
          {receipt && row('Delivery note', receipt.supplierDeliveryNote)}
          {row('Status', document.status.toLowerCase().replaceAll('_', ' '))}

          <hr className="my-2 border-dashed border-black" />

          {lines.map((line) => (
            <div key={line.id} className="mb-1">
              <p>{line.label}</p>
              {line.sku && <p className="text-[9px]">{line.sku}</p>}
              <div className="flex justify-between gap-2">
                <span>
                  {formatQuantity(line.quantity, format)} × {formatMoney(line.unitCost, format)}
                </span>
                <span>{formatMoney(line.lineTotal, format)}</span>
              </div>
              {/* Goods turned away at the door never arrived, so the slip says
                  so rather than leaving a quantity nobody can account for. */}
              {line.rejected && (
                <p className="text-[9px]">
                  {formatQuantity(line.rejected, format)} rejected
                </p>
              )}
            </div>
          ))}

          <hr className="my-2 border-black" />
          <div className="flex justify-between gap-2 text-xs font-bold">
            <span>{isReceipt ? 'RECEIVED' : 'ORDERED'}</span>
            <span>{formatMoney(total, format)}</span>
          </div>
          <hr className="my-2 border-black" />

          <div className="mt-4">
            <p>Received by ______________________</p>
            <p className="mt-3">Signature ________________________</p>
          </div>

          <p className="mt-3 text-center text-[9px]">
            Printed {new Date().toLocaleString()}
          </p>
        </div>
      )}
    </Modal>
  );
}
